import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { LOLCharacterModel } from "../models/lol_character";
import styled from "styled-components";
import { Link, useSearchParams } from "react-router-dom";
import { useLanguage } from "../stores/language_store";
import { translate, Translate } from "../components/translate";

export function LOLCharacters() {
  const [characters, setCharacters] = useState<LOLCharacterModel[]>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterCharacters, setFilterCharacters] = useState(
    searchParams.get("query") ?? ""
  );
  const [filterRole, setFilterRole] = useState("");
  const language = useLanguage();

  const listFilterCharacters = useMemo<LOLCharacterModel[] | undefined>(() => {
    return characters?.filter(
      (c) =>
        c.name.toLowerCase().includes(filterCharacters.toLowerCase()) &&
        (filterRole === "" || c.tags.includes(filterRole))
    );
  }, [characters, filterCharacters, filterRole]);

  const listRoleCharacters = useMemo<string[]>(() => {
    const roles: string[] = [];
    if (characters !== undefined) {
      for (const character of characters) {
        for (const tag of character.tags) {
          if (!roles.includes(tag)) {
            roles.push(tag);
          }
        }
      }
    }
    return roles;
  }, [characters]);

  function fetchCharacters() {
    let lang = "fr_FR";
    if (language === "english") {
      lang = "en_US";
    }
    fetch(
      `https://ddragon.leagueoflegends.com/cdn/15.2.1/data/${lang}/champion.json`
    )
      .then((response) => response.json())
      .then((raw) => {
        // console.log(raw.data);
        setCharacters(Object.values(raw.data));
      })
      .catch((error) =>
        console.log("Erreur lors du chargement des personnages :", error)
      );
  }
  const onFilterChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setFilterCharacters(e.target.value);
      setSearchParams((prev) => {
        prev.set("query", e.target.value);
        return prev;
      });
    },
    [setSearchParams]
  );

  const onRoleChange = (e: ChangeEvent<HTMLSelectElement>) =>
    setFilterRole(e.target.value);

  useEffect(() => {
    fetchCharacters();
  }, []);

  return (
    <GlobalContainer>
      <InputContainer>
        <Input
          type="text"
          placeholder={translate("name_filter", language)}
          value={filterCharacters}
          onChange={onFilterChange}
        />
        <SelectContainer>
          <Label>
            <Translate value="role_filter" />
          </Label>
          <Select onChange={onRoleChange}>
            <option value=""></option>
            {listRoleCharacters.map((role) => (
              <option key={role} value={role}>
                <Translate value={`lol_tag_${role.toLocaleLowerCase()}`} />
              </option>
            ))}
          </Select>
        </SelectContainer>
      </InputContainer>
      <CharacterContainer>
        {listFilterCharacters === undefined ? (
          <div>Loading...</div>
        ) : (
          <>
            {listFilterCharacters.map((character) => (
              <CharacterWrapper
                key={character.name}
                to={`/characters-lol/${character.id}`}
              >
                <Character
                  src={`https://ddragon.leagueoflegends.com/cdn/15.2.1/img/${character.image.group}/${character.image.full}`}
                  alt={character.name}
                />
                <Overlay>{character.name}</Overlay>
              </CharacterWrapper>
            ))}
          </>
        )}
      </CharacterContainer>
    </GlobalContainer>
  );
}

const CharacterContainer = styled.div`
  gap: 5px;
  padding: 20px;
  width: 100%;
  display: grid;
  justify-items: center;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
`;

const Character = styled.img`
  width: 100%;
  height: auto;
  object-fit: cover;
`;

const GlobalContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6); /* Fond sombre semi-transparent */
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 18px;
  font-weight: bold;
  opacity: 0;
  transition: opacity 0.3s ease;
`;

const CharacterWrapper = styled(Link)`
  position: relative;
  cursor: pointer;
  &:hover ${Overlay} {
    opacity: 1;
  }
`;

const Input = styled.input`
  text-align: center;
  height: 30px;
  width: 150px;
  margin-top: 19px;
  border: 2px solid #3498db;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  outline: none;
  &:hover {
    border-color: #2980b9;
  }
  &:focus {
    border-color: #1abc9c;
    box-shadow: 0 0 5px rgba(26, 188, 156, 0.5);
  }
`;

const Select = styled.select`
  width: 150px;
  height: 30px;
  padding-left: 10px;
  border: 2px solid #3498db;
  border-radius: 8px;
  font-size: 16px;
  color: #333;
  cursor: pointer;
  outline: none;
  &:hover {
    border-color: #2980b9;
  }

  &:focus {
    border-color: #1abc9c;
    box-shadow: 0 0 5px rgba(26, 188, 156, 0.5);
  }
`;

const InputContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  text-align: center;
`;
